//import 'rxjs/add/operator/do';

import { HttpEvent, HttpHandler, HttpHeaders, HttpInterceptor, HttpRequest, HttpErrorResponse } from "@angular/common/http";
import { Injectable, Injector, inject } from "@angular/core";
import { CLIENT_CONFIG } from '@app/client-config';
import { UserService } from "@app/services";
import { Observable, from, of } from "rxjs";
import { map, tap, switchMap, catchError } from 'rxjs/operators';
//import 'rxjs/add/operator/catch';
import {Router} from '@angular/router';
import { environment } from '../../../environments/environment';
import { MsalService } from '@azure/msal-angular';

/**
 * Interceptor replaces some of the auth-http.service.ts functionality.
 */
@Injectable()
export class OutdoorAccessApiInterceptor implements HttpInterceptor {
  private readonly _componentName = 'OutdoorAccessApiInterceptor';

  private apiBaseUrl = environment.apiUrl;
  private readonly clientConfig = inject(CLIENT_CONFIG);

  /**
   * The default content type to use
   */
  private defaultContentType = 'application/json; charset=utf-8';

  public constructor(private msalService: MsalService, private injector: Injector) {}

  /**
   * Is verbose logging enabledisApiUrl
   */
  private get verboseLogging(): boolean {
    return false;
    //return true;
  }

    /**
   * Intercepts an HTTP Request
   * @param req
   * @param next
   */
  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // console.log(`${this._className} intercept method: ${req.method}, url: ${req.url}`);

    // If this isn't an API URL, we don't do anything
    // and just handle the request normally.
    if (!this.isOaApiCall(req.url)) {
      return next.handle(req);
    }

    // Request URL is an API Url, so we apply the intercept changes:
    const interceptUrl = this.getUrl(req.url);
    let account = null;
    try {
      account = this.msalService.instance.getActiveAccount();
    } catch {
      // MSAL not yet initialized (e.g. early app startup requests); proceed without token
    }

    const buildAndSend = (accessToken: string | null) => {
      let headers = new HttpHeaders();
      if (accessToken) {
        headers = headers.set('Authorization', `Bearer ${accessToken}`);
      }
      // Do not force JSON content type for multipart uploads; browser sets boundary for FormData.
      if (!(req.body instanceof FormData)) {
        headers = headers.set('Content-Type', this.defaultContentType);
      }
      headers = headers.set('Accept', 'application/json');
      const cloneRequest = req.clone({ url: interceptUrl, headers });
      return next.handle(cloneRequest).pipe(
        tap({
          next: () => {},
          error: (err) => {
            if (err instanceof HttpErrorResponse && (err.status === 401 || err.status === 403)) {
              this.injector.get(UserService).signout();
              this.injector.get(Router).navigateByUrl(`/user/signin`);
            }
          }
        })
      );
    };

    const accessToken$ = account
      ? from(
          this.msalService.instance.acquireTokenSilent({
            account,
            scopes: this.clientConfig.apiScopes
          })
        ).pipe(
          map(result => result.accessToken),
          catchError(() => of(null))
        )
      : of(null);

    return accessToken$.pipe(
      switchMap(accessToken => buildAndSend(accessToken))
    );
  }

  /**
   * Tests to see if the URL is an API url
   * @param url
   */
  private isOaApiCall(url: string): boolean {
    // if this is already an API Url, return true
    if (url.startsWith(this.apiBaseUrl)) {
      if (this.verboseLogging) {
        console.log(`${this._componentName}.isApiUrl - return true because startsWith base url.`, url);
      }

      return true;
    }

    // Not an API Url, leave it alone.
    if (url.startsWith('http')) {
      if (this.verboseLogging) {
        console.log(`${this._componentName}.isApiUrl - return false because does not start with http.`, url);
      }
      return false;
    }

    if (this.verboseLogging) {
      console.log(`${this._componentName}.isApiUrl - return true because no other falsy conditions met.`, url);
    }
    return true;
  }

  /**
   * Modifies the URL, if needed, by appended the API Base URL to the beginning of the string.
   * @param url
   */
  private getUrl(url: string): string {
    if (url.startsWith(this.apiBaseUrl)) {
      return url;
    }

    if (this.isOaApiCall(url)) {
      if (url.startsWith('api')) {
        let resourcePath = url.substring(3);

        if (this.apiBaseUrl.endsWith('/') && resourcePath.startsWith('/')) {
          resourcePath = url.substring(4);
        }

        return `${this.apiBaseUrl}${resourcePath}`;
      } else {
        return `${this.apiBaseUrl}${url}`;
      }
    } else {
      return url;
    }
  }
}
